<script setup lang="ts">
import { inject, ref } from "vue";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import AnimatedBox from "../AnimatedBox.vue";
import DMSuiteIcon from "@/components/iconify/DMSuiteIcon.vue";

defineProps<{
  stats: string | number;
  title: string;
  description: string;
  icon: any;
  iconWrapClass?: string;
  iconClass?: string;
  trendText?: string;
  trendClass?: string;
  tooltip?: string;
}>();

const comfortMode = inject<any>("comfortMode", ref("comfortable"));
</script>

<template>
  <AnimatedBox>
    <Card
      :class="
        cn(
          'group flex flex-col justify-between w-full rounded-3xl border border-slate-100 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_36px_rgba(15,23,42,0.05)] cursor-pointer text-left',
          comfortMode === 'comfortable' ? 'h-[13rem] p-6' : 'h-[11rem] p-5'
        )
      "
      :title="tooltip"
    >
      <div :class="comfortMode === 'comfortable' ? 'space-y-5' : 'space-y-4'">
        <!-- Top: Icon & Title -->
        <div class="flex items-center gap-3">
          <div
            :class="
              cn(
                'flex items-center justify-center border shrink-0',
                comfortMode === 'comfortable'
                  ? 'h-12 w-12 rounded-2xl'
                  : 'h-10 w-10 rounded-xl',
                iconWrapClass || 'bg-slate-50 text-slate-600 border-slate-100'
              )
            "
          >
            <DMSuiteIcon
              v-if="typeof icon === 'string'"
              :icon="icon"
              :size="comfortMode === 'comfortable' ? 24 : 22"
              :custom-class="iconClass"
            />
            <component
              :is="icon"
              v-else
              :class="
                cn(
                  comfortMode === 'comfortable' ? 'size-6' : 'size-5',
                  iconClass
                )
              "
            />
          </div>
          <span
            :class="
              cn(
                'tracking-tight',
                comfortMode === 'comfortable'
                  ? 'text-base font-bold text-slate-900'
                  : 'text-sm font-extrabold text-slate-800'
              )
            "
          >
            {{ title }}
          </span>
        </div>

        <!-- Middle: Stats & Description -->
        <div
          :class="comfortMode === 'comfortable' ? 'space-y-1.5' : 'space-y-1'"
        >
          <p
            :class="
              cn(
                'font-black text-slate-800 tracking-tight leading-none',
                comfortMode === 'comfortable' ? 'text-4xl' : 'text-3xl'
              )
            "
          >
            {{ stats }}
          </p>
          <p
            :class="
              cn(
                comfortMode === 'comfortable'
                  ? 'text-sm font-medium text-slate-600'
                  : 'text-[0.82rem] font-extrabold text-slate-400'
              )
            "
          >
            {{ description }}
          </p>
        </div>
      </div>

      <!-- Bottom: Trend -->
      <div
        v-if="trendText"
        :class="
          cn(
            'font-bold mt-1 flex items-center gap-1',
            comfortMode === 'comfortable' ? 'text-xs' : 'text-[0.76rem]',
            trendClass || 'text-slate-400'
          )
        "
      >
        {{ trendText }}
      </div>
    </Card>
  </AnimatedBox>
</template>
